import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Facebook, Instagram, Star } from "lucide-react";
import { api, money, errMsg } from "@/lib/api";

const STATUS: Record<string, string> = { available: "Credited", applied: "Redeemed", held: "Reserved for checkout", pending: "Pending" };
const day = (s: string) => new Date(s).toLocaleDateString("en-AU", { dateStyle: "medium" });

const Stat = ({ label, value, sub, testId, strong }: any) => (
  <div className={`p-6 border ${strong ? "border-neutral-900 bg-white" : "border-neutral-200 bg-white/60"}`} data-testid={testId}>
    <p className="eyebrow text-neutral-500">{label}</p>
    <p className="font-display text-4xl mt-3" data-testid={`${testId}-value`}>{value}</p>
    {sub && <p className="text-xs text-neutral-500 mt-2">{sub}</p>}
  </div>
);

function QuickEarn({ d, onDone }: any) {
  const p = d.program;
  const follow = async (k: string) => {
    window.open(p[`${k}_url`], "_blank", "noopener");
    try { const r = await api.post(`/rewards/social/${k}`); if (!r.data.already) toast.success(`${money(r.data.amount)} added to your balance`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const btn = "flex items-center gap-2 border border-neutral-900 rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] hover:bg-neutral-900 hover:text-white transition-colors";
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="vip-quick-earn">
      <p className="eyebrow mr-2">Earn more</p>
      {p.facebook > 0 && !d.claimed.facebook && <button onClick={() => follow("facebook")} className={btn} data-testid="vip-follow-facebook"><Facebook size={14} />Follow on Facebook · {money(p.facebook)}</button>}
      {p.instagram > 0 && !d.claimed.instagram && <button onClick={() => follow("instagram")} className={btn} data-testid="vip-follow-instagram"><Instagram size={14} />Follow on Instagram · {money(p.instagram)}</button>}
      {p.review > 0 && <span className="flex items-center gap-2 text-xs text-neutral-600"><Star size={14} />Review a piece you own · {money(p.review)}, or {money(p.review_photo)} with a photo or video</span>}
      <Link to="/pages/rewards" className="text-xs underline ml-auto" data-testid="vip-how-it-works-link">How Somnio VIP works</Link>
    </div>
  );
}

function History({ entries }: any) {
  if (!entries.length) return <p className="text-sm text-neutral-500" data-testid="vip-history-empty">No rewards activity yet. Every A$20 you spend earns A$1.</p>;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="vip-history">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left"><tr>{["Date", "Activity", "Status", "Amount"].map((h) => <th key={h} className={`px-3 sm:px-4 py-3 eyebrow text-[10px] text-neutral-500 font-normal ${h === "Status" ? "hidden sm:table-cell" : ""} ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-neutral-100">
          {entries.map((e: any) => (
            <tr key={e.id} data-testid={`vip-entry-${e.kind}`}>
              <td className="px-3 sm:px-4 py-3 text-xs whitespace-nowrap align-top">{day(e.created_at)}</td>
              <td className="px-3 sm:px-4 py-3">{e.label}{e.order_number && <span className="text-neutral-500"> · {e.order_number}</span>}{e.note && e.kind !== "purchase" && <div className="text-xs text-neutral-500">{e.note}</div>}
                <div className="sm:hidden text-xs text-neutral-500 mt-0.5">{e.bank && e.status === "held" ? "Reserved — awaiting bank transfer" : STATUS[e.status] || e.status}{e.status === "pending" && e.available_at && ` · available ${day(e.available_at)}`}</div></td>
              <td className="hidden sm:table-cell px-4 py-3 text-xs">{e.bank && e.status === "held" ? "Reserved — awaiting bank transfer" : STATUS[e.status] || e.status}{e.status === "pending" && e.available_at && <div className="text-neutral-500">Available {day(e.available_at)}</div>}</td>
              <td className={`px-3 sm:px-4 py-3 text-right whitespace-nowrap align-top ${e.amount < 0 ? "" : "text-emerald-700"}`}>{e.amount < 0 ? "−" : "+"}{money(Math.abs(e.amount))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function VipAccount() {
  const [d, setD] = useState<any>(null);
  const load = () => api.get("/rewards/me").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <div className="space-y-10" data-testid="vip-account">
      <div className="grid sm:grid-cols-3 gap-5">
        <Stat label="Available to spend" value={money(d.available)} sub="Apply it at checkout like cash" testId="vip-available" strong />
        <Stat label="Pending" value={money(d.pending)} sub={`Purchase rewards unlock ${d.program.pending_days} days after purchase`} testId="vip-pending" />
        <Stat label="Lifetime earned" value={money(d.lifetime)} testId="vip-lifetime" />
      </div>
      <QuickEarn d={d} onDone={load} />
      <History entries={d.entries} />
    </div>
  );
}
